import { BuilderBlocks, Builder, withChildren } from '@builder.io/react';
import { Slot } from '@radix-ui/react-slot';
import React, { type ReactNode } from 'react';

import { ProgressiveImage } from '@videoblocks/storywind';

import { lazyLoadDefaultConfig } from '../../constants';
import { generateGradient } from '../../utils/gradientUtils';

type FontFamily = 'polysans' | 'inter';
type FontSize =
  | 'xs'
  | 'sm'
  | 'base'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl';
type AssetCardFontWeight = 'regular' | 'bold' | 'semibold';

export interface AssetTypeCardProps {
  height: string;
  backgroundType: 'image' | 'video';
  backgroundImage?: string;
  backgroundVideo?: string;
  posterImage?: string;
  backgroundPosition: string;
  imageAlt?: string;
  showGradient?: boolean;
  gradientStartOpacity?: number;
  gradientEndOpacity?: number;
  title?: string;
  titleFontSize?: FontSize;
  titleFontWeight?: AssetCardFontWeight;
  titleFontFamily?: FontFamily;
  titleColor?: string;
  lazyLoadMedia?: boolean;
  link?: string;
  builderBlock: { id: string; '@type': '@builder.io/sdk:Element' };
  children: ReactNode;
}

export default function AssetTypeCardComponent({
  height = '344px',
  backgroundType = 'image',
  backgroundImage,
  backgroundVideo,
  posterImage,
  backgroundPosition = 'cover',
  imageAlt = '',
  showGradient = true,
  gradientStartOpacity = 0,
  gradientEndOpacity = 20,
  title,
  titleFontSize = 'xl',
  titleFontWeight = 'semibold',
  titleFontFamily = 'polysans',
  titleColor = '#ffffff',
  lazyLoadMedia = true,
  link,
  builderBlock,
  children,
}: AssetTypeCardProps) {
  const getObjectFitClass = (position: string): string => {
    const positionMap: Record<string, string> = {
      cover: 'object-cover',
      contain: 'object-contain',
      fill: 'object-fill',
      center: 'object-center',
      top: 'object-top',
      bottom: 'object-bottom',
      left: 'object-left',
      right: 'object-right',
    };
    return positionMap[position] || 'object-cover';
  };

  const getFontFamilyClass = (family: FontFamily): string => {
    const fontFamilyMap: Record<FontFamily, string> = {
      polysans: 'font-polysans',
      inter: 'font-inter',
    };
    return fontFamilyMap[family] || 'font-polysans';
  };

  const getFontSizeClass = (size: FontSize): string => {
    const fontSizeMap: Record<FontSize, string> = {
      xs: 'text-xs',
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl',
    };
    return fontSizeMap[size] || 'text-xl';
  };

  const getFontWeightClass = (weight: AssetCardFontWeight): string => {
    const weightMap: Record<AssetCardFontWeight, string> = {
      regular: 'font-normal',
      bold: 'font-bold',
      semibold: 'font-semibold',
    };
    return weightMap[weight] || 'font-bold';
  };

  const objectFitClass = getObjectFitClass(backgroundPosition);
  const gradientStyle = generateGradient(
    gradientStartOpacity,
    gradientEndOpacity
  );
  const fontFamilyClass = getFontFamilyClass(titleFontFamily);
  const fontSizeClass = getFontSizeClass(titleFontSize);
  const fontWeightClass = getFontWeightClass(titleFontWeight);

  const isInternalLink = (): boolean => {
    const url = link;
    if (!url) return false;
    if (url.startsWith('/') || !url.startsWith('http')) return true;

    try {
      const parsed = new URL(url);
      if (typeof window !== 'undefined') {
        return parsed.origin === window.location.origin;
      }
      return parsed.hostname.endsWith('storyblocks.com');
    } catch {
      return true;
    }
  };
  const Wrapper: React.ElementType = link ? 'a' : Slot;

  const wrapperProps = link
    ? isInternalLink()
      ? {
          href: link,
          className: 'no-underline hover:no-underline cursor-pointer',
        }
      : {
          href: link,
          className: 'no-underline hover:no-underline cursor-pointer',
          target: '_blank',
          rel: 'noopener noreferrer',
        }
    : {};

  return (
    <Wrapper {...wrapperProps}>
      <div
        className="relative overflow-hidden w-full rounded-xl"
        style={{ height }}
      >
        <div className="absolute inset-0 w-full h-full">
          {backgroundType === 'image' && backgroundImage ? (
            <ProgressiveImage
              src={backgroundImage}
              alt={imageAlt}
              shouldLazyLoad={lazyLoadMedia}
              className={`absolute inset-0 w-full h-full ${objectFitClass}`}
            />
          ) : backgroundType === 'video' && backgroundVideo ? (
            <video
              className={`w-full h-full ${objectFitClass}`}
              autoPlay
              muted
              loop
              playsInline
              poster={posterImage ? posterImage : undefined}
              aria-label={imageAlt || 'Background video'}
            >
              <source src={backgroundVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <div className="w-full h-full bg-gray-200" />
          )}
        </div>

        <div className="relative z-10 h-full min-h-[200px] p-6 flex flex-col justify-end">
          {showGradient && (
            <div
              className="absolute inset-0"
              style={{ background: gradientStyle }}
            />
          )}

          <div className="relative z-10">
            {title && (
              <div style={{ color: titleColor }}>
                <h2
                  className={`mb-4 ${fontSizeClass} ${fontWeightClass} ${fontFamilyClass}`}
                >
                  {title}
                </h2>
              </div>
            )}

            <div className="max-h-fit space-y-2">
              <BuilderBlocks
                parentElementId={builderBlock.id}
                dataPath="this.children"
                blocks={children}
              />
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
}

const AssetTypeCard = withChildren(AssetTypeCardComponent);
Builder.registerComponent(AssetTypeCard, {
  name: 'Asset Type Card',
  inputs: [
    {
      name: 'height',
      type: 'string',
      enum: [
        { label: 'xs', value: '80px' },
        { label: 'sm', value: '96px' },
        { label: 'md', value: '200px' },
        { label: 'lg', value: '344px' },
        { label: 'xl', value: '456px' },
        { label: 'xxl', value: '600px' },
      ],
      defaultValue: '344px',
      helperText: 'Card height (choose from preset values)',
    },
    {
      name: 'backgroundType',
      type: 'string',
      enum: [
        { label: 'Image', value: 'image' },
        { label: 'Video', value: 'video' },
      ],
      required: true,
      defaultValue: 'image',
      helperText: 'Choose whether to use an image or video as background',
    },
    {
      name: 'backgroundImage',
      type: 'file',
      allowedFileTypes: ['jpg', 'jpeg', 'png', 'webp'],
      defaultValue: '',
      helperText: 'Background image (required if background type is image)',
      showIf: (options) => options.get('backgroundType') === 'image',
    },
    {
      name: 'backgroundVideo',
      type: 'file',
      allowedFileTypes: ['mp4', 'webm'],
      defaultValue: '',
      helperText: 'Background video (required if background type is video)',
      showIf: (options) => options.get('backgroundType') === 'video',
    },
    {
      name: 'posterImage',
      type: 'file',
      allowedFileTypes: ['jpg', 'jpeg', 'png', 'webp'],
      defaultValue: '',
      helperText: 'Poster/preview image for video background (optional)',
      showIf: (options) => options.get('backgroundType') === 'video',
    },
    {
      name: 'backgroundPosition',
      type: 'string',
      enum: [
        { label: 'Cover', value: 'cover' },
        { label: 'Contain', value: 'contain' },
        { label: 'Fill', value: 'fill' },
        { label: 'Center', value: 'center' },
        { label: 'Top', value: 'top' },
        { label: 'Bottom', value: 'bottom' },
        { label: 'Left', value: 'left' },
        { label: 'Right', value: 'right' },
      ],
      defaultValue: 'cover',
      helperText:
        'How the background image/video should be positioned and sized',
    },
    {
      name: 'imageAlt',
      type: 'string',
      defaultValue: '',
      helperText:
        'Alternative text for accessibility and SEO (applies to background images and poster images)',
    },
    {
      name: 'showGradient',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Show gradient overlay for better text readability',
    },
    {
      name: 'gradientStartOpacity',
      type: 'number',
      defaultValue: 0,
      helperText: 'Gradient start opacity (0-100%)',
      showIf: (options) => options.get('showGradient'),
    },
    {
      name: 'gradientEndOpacity',
      type: 'number',
      defaultValue: 20,
      helperText: 'Gradient end opacity (0-100%)',
      showIf: (options) => options.get('showGradient'),
    },
    {
      name: 'title',
      type: 'string',
      defaultValue: '',
      helperText: 'Optional title text displayed over the background',
    },
    {
      name: 'titleFontFamily',
      type: 'string',
      enum: [
        { label: 'Inter', value: 'inter' },
        { label: 'Polysans', value: 'polysans' },
      ],
      defaultValue: 'polysans',
      helperText:
        'Font family for the title (step 1: choose the font family first)',
    },
    {
      name: 'titleFontSize',
      type: 'string',
      enum: ['xs', 'sm', 'base', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl'],
      defaultValue: 'xl',
      helperText: 'Font size for the title (step 2: choose the size)',
    },
    {
      name: 'titleFontWeight',
      type: 'string',
      enum: [
        { label: 'Regular', value: 'regular' },
        { label: 'Semibold', value: 'semibold' },
        { label: 'Bold', value: 'bold' },
      ],
      defaultValue: 'semibold',
      helperText:
        'Font weight for the title (step 3: choose from weights compatible with your font family and size)',
    },
    {
      name: 'titleColor',
      type: 'color',
      defaultValue: '#ffffff',
      helperText: 'Color for the title text',
    },
    {
      name: 'link',
      type: 'string',
      defaultValue: '',
      helperText:
        'Optional link for the card. Use a relative path for internal routes or a full URL for external links.',
    },
    {
      ...lazyLoadDefaultConfig,
    },
    {
      name: 'Identifier',
      type: 'string',
      enum: ['Stockblocks'],
      defaultValue: 'Stockblocks',
      advanced: true,
      onChange: (_, context) => {
        context.set('Identifier', 'Stockblocks');
      },
      helperText: "Identifier for the repo, don't change this",
    },
  ],
  canHaveChildren: true,
  defaultChildren: [],
});
